// client/src/components/media/MentionInput.jsx
// Input with @mention support for platform handles (Twitter, Instagram, etc.).
// Supports both textarea (default) and single-line input modes.
// Renders a highlight backdrop so @handles appear styled as links.
// Clicking a highlighted @handle opens a popover with profile links for each platform.
// Stores recently-used handles in localStorage for quick re-use.

import React, { useState, useRef, useEffect, useCallback } from 'react';

const MENTION_TRIGGER = /@([a-zA-Z0-9_.]*)$/;
const MENTION_GLOBAL  = /@([a-zA-Z][a-zA-Z0-9_.]*)/g;
const STORAGE_KEY     = 'onscreen_recent_handles';
const MAX_RECENT      = 20;

// ---------------------------------------------------------------------------
// Platform profile URL builders
// ---------------------------------------------------------------------------
const PLATFORMS = [
  { key: 'twitter',   label: 'X / Twitter',  icon: '𝕏', url: h => `https://x.com/${h}` },
  { key: 'instagram', label: 'Instagram',     icon: '📷', url: h => `https://instagram.com/${h}` },
  { key: 'tiktok',    label: 'TikTok',        icon: '🎵', url: h => `https://tiktok.com/@${h}` },
  { key: 'facebook',  label: 'Facebook',      icon: '📘', url: h => `https://facebook.com/${h}` },
  { key: 'linkedin',  label: 'LinkedIn',      icon: '💼', url: h => `https://linkedin.com/in/${h}` },
  { key: 'youtube',   label: 'YouTube',       icon: '▶️', url: h => `https://youtube.com/@${h}` },
  { key: 'bluesky',   label: 'Bluesky',       icon: '🦋', url: h => `https://bsky.app/profile/${h}` },
  { key: 'reddit',    label: 'Reddit',        icon: '🤖', url: h => `https://reddit.com/user/${h}` },
];

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------
function getRecentHandles() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
  catch { return []; }
}
function saveHandle(handle) {
  if (!handle) return;
  const clean = handle.replace(/^@/, '').trim();
  if (!clean) return;
  try {
    let list = getRecentHandles().filter(h => h !== clean);
    list.unshift(clean);
    if (list.length > MAX_RECENT) list = list.slice(0, MAX_RECENT);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Build highlighted HTML — @handles become clickable marks
// ---------------------------------------------------------------------------
function buildHighlightMarkup(text) {
  if (!text) return '\n';
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const html = escaped.replace(
    /@([a-zA-Z][a-zA-Z0-9_.]*)/g,
    '<mark class="mi-highlight" data-handle="$1">@$1</mark>'
  );
  return html + '\n';
}

// ---------------------------------------------------------------------------
// Profile Links Popover
// ---------------------------------------------------------------------------
function ProfilePopover({ handle, anchorRect, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  if (!handle || !anchorRect) return null;

  const top = anchorRect.bottom + 4;
  const left = Math.max(8, Math.min(anchorRect.left, window.innerWidth - 240));

  return (
    <div ref={ref} className="mi-profile-popover" style={{ top, left }}>
      <div className="mi-pop-header">
        <span className="mi-pop-handle">@{handle}</span>
        <button className="mi-pop-close" onClick={onClose}>&times;</button>
      </div>
      <div className="mi-pop-subtitle">View profile on</div>
      <div className="mi-pop-links">
        {PLATFORMS.map(p => (
          <a
            key={p.key}
            href={p.url(handle)}
            target="_blank"
            rel="noopener noreferrer"
            className="mi-pop-link"
            onClick={onClose}
          >
            <span className="mi-pop-icon">{p.icon}</span>
            {p.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function MentionInput({
  value,
  onChange,
  placeholder = '',
  rows = 5,
  className = 'form-control',
  singleLine = false,
  style,
  dropdownPosition = 'above',
}) {
  const inputRef     = useRef(null);
  const backdropRef  = useRef(null);
  const dropdownRef  = useRef(null);
  const [suggestions, setSuggestions]   = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIdx, setSelectedIdx]   = useState(0);
  const [popover, setPopover]           = useState(null); // { handle, rect }

  // ── detect @trigger at cursor ──────────────────────────────────────────────
  const detectMention = useCallback(() => {
    const el = inputRef.current;
    if (!el) return null;
    const before = value.slice(0, el.selectionStart);
    const m = before.match(MENTION_TRIGGER);
    return m ? m[1] : null;
  }, [value]);

  // ── filter recent handles ──────────────────────────────────────────────────
  useEffect(() => {
    const q = detectMention();
    if (q !== null) {
      const recent = getRecentHandles();
      const filtered = q.length === 0
        ? recent.slice(0, 8)
        : recent.filter(h => h.toLowerCase().startsWith(q.toLowerCase())).slice(0, 8);
      setSuggestions(filtered);
      setShowDropdown(filtered.length > 0);
      setSelectedIdx(0);
    } else {
      setShowDropdown(false);
      setSuggestions([]);
    }
  }, [value, detectMention]);

  // ── insert selected handle ─────────────────────────────────────────────────
  const insertHandle = useCallback((handle) => {
    const el = inputRef.current;
    if (!el) return;
    const cur = el.selectionStart;
    const before = value.slice(0, cur);
    const after  = value.slice(cur);
    const m = before.match(MENTION_TRIGGER);
    if (!m) return;
    const at = cur - m[0].length;
    onChange(before.slice(0, at) + `@${handle} ` + after);
    saveHandle(handle);
    setShowDropdown(false);
    setSuggestions([]);
    requestAnimationFrame(() => {
      el.focus();
      const pos = at + handle.length + 2;
      el.setSelectionRange(pos, pos);
    });
  }, [value, onChange]);

  // ── blur → persist handles ─────────────────────────────────────────────────
  const handleBlur = useCallback(() => {
    const found = value.match(MENTION_GLOBAL);
    if (found) found.forEach(m => saveHandle(m.slice(1)));
    setTimeout(() => setShowDropdown(false), 150);
  }, [value]);

  // ── keyboard nav ───────────────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (!showDropdown || !suggestions.length) return;
    if (e.key === 'ArrowDown')  { e.preventDefault(); setSelectedIdx(i => (i + 1) % suggestions.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIdx(i => (i - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { if (suggestions[selectedIdx]) { e.preventDefault(); insertHandle(suggestions[selectedIdx]); } }
    else if (e.key === 'Escape') { setShowDropdown(false); }
  };

  // ── close dropdown on outside click ────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── handle click on highlighted @handle in backdrop ────────────────────────
  const handleBackdropClick = useCallback((e) => {
    const mark = e.target.closest('.mi-highlight');
    if (!mark) return;
    const handle = mark.getAttribute('data-handle');
    if (!handle) return;
    const rect = mark.getBoundingClientRect();
    setPopover({ handle, rect });
  }, []);

  // ── sync backdrop scroll with textarea ─────────────────────────────────────
  const syncScroll = () => {
    if (backdropRef.current && inputRef.current) {
      backdropRef.current.scrollTop  = inputRef.current.scrollTop;
      backdropRef.current.scrollLeft = inputRef.current.scrollLeft;
    }
  };

  // ── dropdown styles ────────────────────────────────────────────────────────
  const isAbove = dropdownPosition === 'above';
  const ddStyles = {
    position: 'absolute',
    ...(isAbove ? { bottom: '100%', marginBottom: 4 } : { top: '100%', marginTop: 4 }),
    left: 0, right: 0, maxHeight: 200, overflowY: 'auto',
    background: '#fff', border: '1px solid #dee2e6', borderRadius: 6,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 1050,
  };

  // ── shared dropdown markup ─────────────────────────────────────────────────
  const dropdownMarkup = showDropdown && suggestions.length > 0 && (
    <div ref={dropdownRef} style={ddStyles}>
      <div style={{ padding: '4px 12px', fontSize: 11, color: '#6c757d', borderBottom: '1px solid #f0f0f0', background: '#f8f9fa' }}>
        Recent handles
      </div>
      {suggestions.map((h, i) => (
        <div key={h} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 14, fontWeight: 500, background: i === selectedIdx ? '#e8f0fe' : 'transparent', borderBottom: '1px solid #f0f0f0' }}
          onMouseEnter={() => setSelectedIdx(i)}
          onMouseDown={e => { e.preventDefault(); insertHandle(h); }}>
          @{h}
        </div>
      ))}
    </div>
  );

  // ── single-line mode ───────────────────────────────────────────────────────
  if (singleLine) {
    return (
      <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
        <input
          ref={inputRef} type="text" className={className} value={value}
          onChange={e => onChange(e.target.value)} onKeyDown={handleKeyDown}
          onBlur={handleBlur} placeholder={placeholder} style={style}
        />
        {dropdownMarkup}
      </div>
    );
  }

  // ── multi-line mode with highlight backdrop ────────────────────────────────
  return (
    <div style={{ position: 'relative' }}>
      {/* Highlight backdrop — marks are clickable */}
      <div
        ref={backdropRef}
        className="mi-backdrop"
        onClick={handleBackdropClick}
        dangerouslySetInnerHTML={{ __html: buildHighlightMarkup(value) }}
      />
      {/* Actual textarea */}
      <textarea
        ref={inputRef}
        className={`${className} mi-textarea`}
        rows={rows}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onScroll={syncScroll}
        placeholder={placeholder}
        style={style}
      />

      {dropdownMarkup}

      {/* Profile links popover */}
      {popover && (
        <ProfilePopover
          handle={popover.handle}
          anchorRect={popover.rect}
          onClose={() => setPopover(null)}
        />
      )}

      <style>{`
        .mi-backdrop {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          padding: 0.375rem 0.75rem;
          font-size: 1rem;
          line-height: 1.5;
          font-family: inherit;
          white-space: pre-wrap;
          word-wrap: break-word;
          overflow: hidden;
          color: transparent;
          pointer-events: none;
          border: 1px solid transparent;
          border-radius: 0.375rem;
          background: #fff;
        }
        .mi-backdrop .mi-highlight {
          color: transparent;
          background: rgba(29,155,240,0.13);
          border-radius: 3px;
          pointer-events: auto;
          cursor: pointer;
          text-decoration: underline;
          text-decoration-color: rgba(29,155,240,0.5);
          text-underline-offset: 2px;
        }
        .mi-backdrop .mi-highlight:hover {
          background: rgba(29,155,240,0.22);
          text-decoration-color: #1d9bf0;
        }
        .mi-textarea {
          position: relative;
          background: transparent !important;
          caret-color: #212529;
        }

        /* ── Profile popover ── */
        .mi-profile-popover {
          position: fixed;
          z-index: 9999;
          width: 230px;
          background: #fff;
          border: 1px solid #dee2e6;
          border-radius: 10px;
          box-shadow: 0 8px 24px rgba(0,0,0,0.18);
          overflow: hidden;
          animation: mi-pop-in 0.12s ease-out;
        }
        @keyframes mi-pop-in {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mi-pop-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 14px 4px;
        }
        .mi-pop-handle {
          font-size: 15px;
          font-weight: 700;
          color: #1d9bf0;
        }
        .mi-pop-close {
          background: none;
          border: none;
          font-size: 20px;
          color: #6c757d;
          cursor: pointer;
          padding: 0 2px;
          line-height: 1;
        }
        .mi-pop-close:hover { color: #212529; }
        .mi-pop-subtitle {
          padding: 0 14px 8px;
          font-size: 11px;
          color: #6c757d;
        }
        .mi-pop-links {
          display: flex;
          flex-direction: column;
          border-top: 1px solid #f0f0f0;
        }
        .mi-pop-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 14px;
          font-size: 13px;
          font-weight: 500;
          color: #212529;
          text-decoration: none;
          transition: background 0.1s;
        }
        .mi-pop-link:hover {
          background: #f0f6ff;
          color: #1d9bf0;
        }
        .mi-pop-icon {
          width: 20px;
          text-align: center;
          font-size: 15px;
        }
      `}</style>
    </div>
  );
}
